/*
  TASK-2066 / TASK-5009 — Central delayed-delivery ATC confirmation popup.

  Fires when a <product-form> marked with data-ils-preorder-active="1"
  is submitted. Everything it shows comes from that same element:

    data-ils-preorder-date      arrival date, already DD.MM.YYYY from Liquid
    data-ils-preorder-product   product title, already localized by Liquid
    data-ils-preorder-image     product featured image (fallback thumbnail)
    data-ils-preorder-url       product URL (locale-prefixed by Liquid)
    data-ils-preorder-variants  JSON [{id,w,s,i}] — weight / sku / image per
                                variant, emitted by snippets/ils-preorder-attrs.liquid

  Works from any surface that emits a <product-form> ATC form:
    - PDP (sections/custom-main-product.liquid sticky ATC)
    - Dawn buy-buttons + quick-add card (snippets/buy-buttons.liquid, card-product.liquid)
    - ILS rail / grid card (snippets/custom-card-product-list.liquid)
    - cart-drawer / cart-page upsell + weight picker, via ILSPreorderPopup.confirm()

  The interceptor runs in capture phase on document — it MUST run before
  Dawn's <product-form>.onSubmitHandler, because that handler kicks off
  the fetch to /cart/add and is bound to the form in the bubbling phase.

  ── TASK-5009 ────────────────────────────────────────────────────────────────
  WHAT CHANGED, AND WHY EACH PIECE IS THE WAY IT IS.

  * NO COPY LIVES HERE ANY MORE. The six hardcoded LOCALE_STRINGS language
    branches (and the `split('-')[0]` locale guess that silently fell back to
    `lv` for anything it did not recognise — including every RU market) are
    gone. All static text is server-rendered from `custom.preorder.*` theme
    locale keys in snippets/custom-preorder-popup.liquid, so Shopify resolves it
    per (market, locale) and the five RU market targets are independent. This
    file only fills the DYNAMIC slots.

  * THE SELECTED VARIANT IS READ AT OPEN TIME from input[name="id"] inside the
    intercepted form, then looked up in the variants map. It is never taken from
    a server-rendered "selected" attribute: the weight tiles swap the variant
    client-side by rewriting that input, so a server-rendered value would be
    stale after the first weight click. Weight and SKU are printed ONLY when the
    map really has them — there is no placeholder, no default, no "113g".

  * BACKDROP AND ESCAPE STILL DO NOTHING and there is still no X. The only two
    exits are the buttons. (There is no backdrop click handler in this file at
    all; Escape is swallowed in capture phase, below.)

  * STATE SURVIVES THE INFO LINK. Clicking "More about delayed delivery" is a
    real navigation, so the pending add would otherwise be stranded. On that
    click the full popup context is written to sessionStorage; on the way back
    it is restored:
      - bfcache hit  → the page, the open dialog and the pending promise are all
        still alive, so restore() finds the dialog already open and only drops
        the saved context;
      - full reload  → the dialog is re-opened with the SAME product, variant,
        weight, SKU and date, and is re-bound to the matching <product-form> in
        the fresh DOM so "Confirm & add" goes through the ordinary Dawn add
        path. If that form is not on the page any more (the cart-drawer upsell
        case) it falls back to one /cart/add.js call.
    Nothing is ever added by merely visiting the information page: the add only
    ever happens inside the confirm handler.

  * ONE ADD PER CONFIRM. `busy` latches on the first confirm click and is only
    released when the dialog opens again, so a double click (or a click plus a
    keyboard activation) cannot fire two adds.
*/
(function () {
  var STORE_KEY = 'ILS_PREORDER_RESTORE_V1';
  var RESTORE_TTL_MS = 30 * 60 * 1000;

  var modal = null;
  var lastConfirmedForm = null;
  var lastActiveButton = null;
  // TASK-2424 — Promise-based confirmation flow used by cart-drawer /
  // cart-page upsell + weight-picker paths, which don't go through a
  // <product-form> submit (they call fetch('/cart/add.js') directly).
  // When set, the document-level click delegator below resolves this
  // instead of falling into the form-based re-fire path.
  var pendingResolver = null;
  // TASK-5009 — the context currently on screen, and the two latches.
  var ctx = null;
  var busy = false;
  var restoredDirect = false;

  function ensureModal() {
    if (modal && document.contains(modal)) return modal;
    modal = document.getElementById('ils-preorder-modal');
    return modal;
  }

  function parseVariants(raw) {
    if (!raw) return [];
    try {
      var a = JSON.parse(raw);
      return Object.prototype.toString.call(a) === '[object Array]' ? a : [];
    } catch (e) {
      return [];
    }
  }

  function pickVariant(list, id) {
    var want = String(id || '');
    if (!want) return null;
    for (var i = 0; i < list.length; i++) {
      if (String(list[i] && list[i].id) === want) return list[i];
    }
    return null;
  }

  // Build the popup context from an intercepted <product-form>.
  function ctxFromForm(pf) {
    var form = pf.querySelector('form');
    var idInput = form && form.querySelector('[name="id"]');
    var qtyInput = form && form.querySelector('[name="quantity"]');
    var variantId = idInput ? String(idInput.value || '') : '';
    var v = pickVariant(parseVariants(pf.getAttribute('data-ils-preorder-variants')), variantId);
    return {
      date: pf.getAttribute('data-ils-preorder-date') || '',
      variantId: variantId,
      quantity: qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1,
      title: pf.getAttribute('data-ils-preorder-product') || '',
      image: (v && v.i) || pf.getAttribute('data-ils-preorder-image') || '',
      weight: (v && v.w) || '',
      sku: (v && v.s) || '',
      url: pf.getAttribute('data-ils-preorder-url') || ''
    };
  }

  // Build the popup context from the object the cart surfaces pass to
  // ILSPreorderPopup.confirm(). A bare string keeps working (date only).
  function ctxFromArg(arg) {
    if (!arg || typeof arg === 'string') {
      return { date: String(arg || ''), variantId: '', quantity: 1, title: '', image: '', weight: '', sku: '', url: '' };
    }
    var v = pickVariant(parseVariants(arg.variants), arg.variantId);
    return {
      date: arg.date || '',
      variantId: String(arg.variantId || ''),
      quantity: parseInt(arg.quantity, 10) || 1,
      title: arg.title || '',
      image: (v && v.i) || arg.image || '',
      weight: (v && v.w) || '',
      sku: (v && v.s) || '',
      url: arg.url || ''
    };
  }

  function paint(c) {
    var m = ensureModal();
    if (!m) return false;

    var dateEl = m.querySelector('[data-ils-po-date]');
    if (dateEl) dateEl.textContent = c.date || '';

    var row = m.querySelector('[data-ils-po-product]');
    var img = m.querySelector('[data-ils-po-image]');
    var name = m.querySelector('[data-ils-po-name]');
    var weight = m.querySelector('[data-ils-po-weight]');
    var sku = m.querySelector('[data-ils-po-sku]');
    var skuVal = m.querySelector('[data-ils-po-sku-value]');

    if (name) name.textContent = c.title || '';
    if (img) {
      if (c.image) {
        img.setAttribute('src', c.image);
        img.setAttribute('alt', c.title || '');
        img.hidden = false;
      } else {
        img.removeAttribute('src');
        img.hidden = true;
      }
    }
    // Weight and SKU are shown ONLY when the real variant carries them.
    if (weight) {
      weight.textContent = c.weight || '';
      weight.hidden = !c.weight;
    }
    if (sku) {
      if (skuVal) skuVal.textContent = c.sku || '';
      sku.hidden = !c.sku;
    }
    if (row) row.hidden = !(c.title || c.image || c.weight || c.sku);
    return true;
  }

  function show(c) {
    if (!paint(c)) return false;
    ctx = c;
    busy = false;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var confirmBtn = modal.querySelector('[data-ils-po-confirm]');
    if (confirmBtn) setTimeout(function () { confirmBtn.focus(); }, 0);
    return true;
  }

  function open(c, pfElement, submitButton) {
    lastConfirmedForm = pfElement || null;
    lastActiveButton = submitButton || null;
    restoredDirect = false;
    show(c);
  }

  function close() {
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function releaseButton(btn, pf) {
    // Dawn locks the submit button while it "loads". If we cancel before the
    // fetch, we need to unlock it manually so the customer can try again.
    if (!btn) return;
    btn.classList.remove('loading');
    btn.removeAttribute('aria-disabled');
    var spinner = pf && pf.querySelector('.loading__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  function reFire(pf) {
    if (!pf) return;
    var form = pf.querySelector('form');
    if (!form) return;
    pf.__ilsPreorderOK = true;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      // Fallback — dispatch a submit event so Dawn's <product-form> handler picks it up.
      var evt = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(evt);
      if (!evt.defaultPrevented) form.submit();
    }
  }

  // ── TASK-5009 — info link: persist, navigate, come back ──────────────────
  function localeRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r === '/' ? '' : r.replace(/\/+$/, '');
  }

  function bareHref() {
    return (location.href || '').split('#')[0];
  }

  function saveRestore() {
    if (!ctx) return;
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ ctx: ctx, href: bareHref(), t: Date.now() }));
    } catch (e) { /* private mode — the popup simply will not come back */ }
  }

  function dropRestore() {
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) {}
  }

  // Re-bind a restored context to a live <product-form> so "Confirm & add"
  // goes through the ordinary Dawn add path with the EXACT same variant.
  function findFormForVariant(variantId) {
    var forms = document.querySelectorAll('product-form[data-ils-preorder-active="1"]');
    for (var i = 0; i < forms.length; i++) {
      var pf = forms[i];
      if (pickVariant(parseVariants(pf.getAttribute('data-ils-preorder-variants')), variantId)) return pf;
      var input = pf.querySelector('[name="id"]');
      if (input && String(input.value) === String(variantId)) return pf;
    }
    return null;
  }

  function restore() {
    var raw = null;
    try { raw = sessionStorage.getItem(STORE_KEY); } catch (e) { return; }
    if (!raw) return;
    var data = null;
    try { data = JSON.parse(raw); } catch (e) { dropRestore(); return; }
    if (!data || !data.ctx || !data.ctx.date) { dropRestore(); return; }
    if (Date.now() - (data.t || 0) > RESTORE_TTL_MS) { dropRestore(); return; }
    // On the information page itself the context must SURVIVE untouched — it is
    // what the way back is going to consume. Dropping it here is how a naive
    // version of this loses the dialog.
    if (data.href !== bareHref()) return;
    // The dialog markup may not be parsed yet on the very first call; a later
    // DOMContentLoaded / pageshow retries, so do not consume the context yet.
    if (!ensureModal()) return;
    dropRestore();
    if (!modal.hidden) return;                               // bfcache already brought it back

    var c = data.ctx;
    var pf = findFormForVariant(c.variantId);
    restoredDirect = !pf;
    pendingResolver = null;
    if (pf) {
      var form = pf.querySelector('form');
      var input = form && form.querySelector('[name="id"]');
      if (input && c.variantId) input.value = c.variantId;
      var qty = form && form.querySelector('[name="quantity"]');
      if (qty && c.quantity > 1) qty.value = c.quantity;
      lastConfirmedForm = pf;
      lastActiveButton = pf.querySelector('[type="submit"]');
    } else {
      lastConfirmedForm = null;
      lastActiveButton = null;
    }
    show(c);
  }

  // Last-resort add for a restored context whose form is gone (cart-drawer
  // upsell). Exactly one /cart/add.js call, then a reload so every cart
  // surface on the page picks the new line up.
  function directAdd(c) {
    var id = parseInt(c.variantId, 10);
    if (!id) { close(); return; }
    fetch(localeRoot() + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: c.quantity || 1 }] })
    })
      .then(function () { window.location.reload(); })
      .catch(function () { close(); });
  }

  // Capture-phase submit interceptor — MUST run before Dawn's product-form
  // handler, which is attached in bubbling phase inside <product-form>'s
  // constructor. stopImmediatePropagation() blocks that handler when we
  // want to defer submission behind the modal.
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var pf = form.closest('product-form');
      if (!pf) return;
      if (pf.dataset.ilsPreorderActive !== '1') return;

      if (pf.__ilsPreorderOK) {
        pf.__ilsPreorderOK = false;
        return; // let Dawn handle it
      }

      var c = ctxFromForm(pf);
      if (!c.date) return;

      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      open(c, pf, pf.querySelector('[type="submit"]'));
    },
    true
  );

  // The info link is a REAL navigation. Persist first, and deliberately do NOT
  // close the dialog: on a bfcache hit the page comes back with it still open.
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (!t.closest('[data-ils-po-info]')) return;
      if (!modal || modal.hidden) return;
      saveRestore();
    },
    true
  );

  window.addEventListener('pageshow', restore);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restore);
  } else {
    restore();
  }

  // Dialog wiring — event delegation off the document so it survives the
  // snippet being rendered before/after any specific script executes.
  // NOTE: there is no backdrop handler here BY DESIGN — clicking the dim
  // backdrop must not close the dialog (owner requirement).
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-ils-po-cancel]')) {
      e.preventDefault();
      if (busy) return;
      busy = true;
      dropRestore();
      ctx = null;
      restoredDirect = false;
      if (pendingResolver) {
        var r = pendingResolver;
        pendingResolver = null;
        close();
        r(false);
        return;
      }
      releaseButton(lastActiveButton, lastConfirmedForm);
      lastConfirmedForm = null;
      lastActiveButton = null;
      close();
      return;
    }
    if (t.closest('[data-ils-po-confirm]')) {
      e.preventDefault();
      if (busy) return;           // one add per open — never two
      busy = true;
      dropRestore();
      var c = ctx;
      ctx = null;
      if (pendingResolver) {
        var r2 = pendingResolver;
        pendingResolver = null;
        close();
        r2(true);
        return;
      }
      if (restoredDirect) {
        restoredDirect = false;
        directAdd(c || {});
        return;
      }
      var pf = lastConfirmedForm;
      close();
      // Clear any "loading" spinner state Dawn set before we cancelled the first submit.
      releaseButton(lastActiveButton, pf);
      lastConfirmedForm = null;
      lastActiveButton = null;
      reFire(pf);
    }
  });

  // TASK-2424 — Public confirmation API for non-form ATC surfaces
  // (cart-drawer «WE RECOMMEND» upsell, cart-drawer weight-picker,
  // cart-page upsell). Resolves true on confirm, false on cancel.
  // Falls through to true (no popup) when the date is missing or the
  // popup DOM isn't on the page, so callers can never deadlock.
  // TASK-5009 — accepts either the legacy date string or the full payload
  // { date, variantId, quantity, title, image, variants, url }.
  window.ILSPreorderPopup = window.ILSPreorderPopup || {};
  window.ILSPreorderPopup.confirm = function (arg) {
    return new Promise(function (resolve) {
      var c = ctxFromArg(arg);
      if (!c.date) { resolve(true); return; }
      if (!ensureModal()) { resolve(true); return; }
      lastConfirmedForm = null;
      lastActiveButton = null;
      restoredDirect = false;
      pendingResolver = resolve;
      if (!show(c)) { pendingResolver = null; resolve(true); }
    });
  };

  // Keep Esc from closing — behaviour matches live site (owner requires
  // an explicit choice). Users can still click "Don't add" to dismiss.
  document.addEventListener('keydown', function (e) {
    if (!modal || modal.hidden) return;
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); }
  }, true);
})();
